import { useHistory, useLocation } from 'react-router-dom'
import React, { useEffect, useMemo } from 'react'
import { CircularProgress, makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'
import { useQuery } from '@tanstack/react-query'
import { fetchUserViews, useAppConfig } from '../../../redux/slices/appConfig'
import SydButton from '../../commonDesign/Button'
import ConfigurableView from '../../pages/admin/ConfigurableView'
import { postNamedQuery } from '../../../service'

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: 'calc(100vh - 64px)',
    width: '100%',
    gap: '2rem'
  },
  header: {
    fontSize: '1.75rem',
    fontFamily: theme.typography.families.bold
  }
}))

export const DocumentVaultLandingPageContent = ({
  redirectText = 'Redirecting to Document Vault...',
  errorLinkText = 'Go Home',
  errorText = 'No Document Vault view found.'
}) => {
  const classes = useStyles()
  const { search } = useLocation()
  const { homeViewPath } = useAppConfig()
  const history = useHistory()

  const currentDocumentId = useMemo(() => {
    const searchParams = new URLSearchParams(search)
    return searchParams.get('documentId') ?? null
  }, [search])

  const { data: docVaultView, isFetched } = useQuery({
    queryKey: ['docVaultViewPath'],
    queryFn: async () => {
      const userViews = await fetchUserViews()
      let docVaultView = userViews?.data?.find(({ tagName }) => tagName === 'documentVault')

      if (!docVaultView) {
        const activeViews = await postNamedQuery('abundanceEngine', 'get-active-views', {})
        docVaultView = activeViews?.data?.rows?.find(({ content }) => content.includes('<DocumentVault'))
      }

      return docVaultView
    }
  })

  const docVaultViewPath = useMemo(() => {
    if (!homeViewPath?.length || !docVaultView) return null
    return [homeViewPath, docVaultView.path, ...(currentDocumentId ? [`?documentId=${currentDocumentId}`] : [])].join('/')
  }, [currentDocumentId, docVaultView, homeViewPath])

  useEffect(() => {
    if (docVaultViewPath?.length) {
      history.replace(docVaultViewPath)
    }
  }, [docVaultViewPath, history])

  if (isFetched && !docVaultViewPath?.length) {
    return (
      <div className={classes.container}>
        <p className={classes.header}>{errorText}</p>
        <SydButton
          variant='primary'
          onClick={() => history.push('/')}
        >
          {errorLinkText}
        </SydButton>
      </div>
    )
  }

  return (
    <div className={classes.container}>
      <CircularProgress size='3rem' />
      <p className={classes.header}>{redirectText}</p>
    </div>
  )
}
DocumentVaultLandingPageContent.propTypes = {
  redirectText: PropTypes.string,
  errorLinkText: PropTypes.string,
  errorText: PropTypes.string
}

export const DocumentVaultLandingPage = () => {
  return (
    <ConfigurableView viewPath='@document-vault-landing-page'>
      <DocumentVaultLandingPageContent
        redirectText='Redirecting to Document Vault...'
        errorLinkText='Go Home'
        errorText='No Document Vault view found.'
      />
    </ConfigurableView>
  )
}
